import useSWR, { type SWRConfiguration } from 'swr';
import denApi from '@/lib/api/denApi';
import {
  ContractAbiDetails,
  ContractAbiDetailsType,
  NetworkType,
} from '@mls-wallet/shared-types';

type ContractAbiParams = {
  address: string | null;
  networkType: NetworkType | null;
};

const fetcher = async (
  params: ContractAbiParams
): Promise<ContractAbiDetails> => {
  return await denApi.get<ContractAbiDetails, ContractAbiParams>(
    'contract/abi-details',
    params
  );
};

/**
 * Hook to fetch contract ABI details including write functions and their parameters.
 *
 * This hook fetches ABI information for a given contract address on a specific network.
 * For proxy contracts, it returns both proxy functions and implementation functions.
 *
 * @param params - Object containing contract address (checksummed) and network type
 * @param swrOptions - Optional SWR configuration options
 * @returns Object containing contract ABI details (name, functions), loading state, error, and refetch function
 */
export function useContractAbi(
  params: ContractAbiParams | null,
  swrOptions?: SWRConfiguration<ContractAbiDetails>
) {
  // Build the SWR key - null will disable fetching
  // Only fetch when we have both address and network type
  const swrKey =
    params && params.address !== null && params.networkType !== null
      ? `contract/abi-details/${params.address}/${params.networkType}`
      : null;

  // Always call useSWR to comply with Rules of Hooks
  // When swrKey is null, SWR won't fetch
  const { data, error, isLoading, mutate } = useSWR<ContractAbiDetails>(
    swrKey,
    swrKey ? () => fetcher(params as ContractAbiParams) : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      ...(swrOptions ?? {}),
    }
  );

  // For ERC-20, prefer token symbol over contract name for display
  const contractDisplayName = (() => {
    if (!data || data.type === ContractAbiDetailsType.ERROR) return null;
    if (data.type === ContractAbiDetailsType.ERC20) {
      return (
        data.tokenMetadata?.symbol ??
        data.tokenMetadata?.name ??
        data.contractName
      );
    }
    return data.contractName;
  })();

  return {
    contractName:
      data && data.type !== ContractAbiDetailsType.ERROR
        ? data.contractName
        : null,
    /** Display name - uses token symbol for ERC-20, otherwise contract name */
    contractDisplayName,
    implementationFunctions:
      data && data.type !== ContractAbiDetailsType.ERROR
        ? data.implementationFunctions
        : [],
    proxyFunctions:
      data && data.type !== ContractAbiDetailsType.ERROR
        ? data.proxyFunctions
        : [],
    errorMessage:
      data?.type === ContractAbiDetailsType.ERROR ? data.message : null,
    errorCode:
      data?.type === ContractAbiDetailsType.ERROR ? data.errorCode : null,
    isLoading,
    error,
    refetch: mutate,
  };
}
